import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut, deleteUser, EmailAuthProvider, reauthenticateWithCredential  } from "firebase/auth";
import './Properties.css'; // Reuse the same CSS file for consistency
import Footer from './components/Footer';

function MyAccount() {
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // For showing save confirmation

  const serverUrl = process.env.REACT_APP_NODE_ENV === 'production'
  ? process.env.REACT_APP_PRODUCTION_SERVER_URL
  : process.env.REACT_APP_LOCAL_SERVER_URL;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe(); // Cleanup subscription
  }, [auth]);

  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      if (!user) {
        setIsLoading(false);
        return;
      }

      const db = getFirestore();
      const userDocRef = doc(db, "userPreferences", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const data = userDocSnap.data();
        setProfile(data);
        setMarketingOptIn(data.marketingOptIn || false); // Pre-fill checkbox based on stored value
      } else {
        console.error("No profile data found for user");
      }
      setIsLoading(false);
    };

    fetchProfile();
  }, [user]);

  const handleMarketingToggle = async () => {
    if (!user) return;
    const newOptInValue = !marketingOptIn;
    setMarketingOptIn(newOptInValue);

    try {
      const db = getFirestore();
      const userDocRef = doc(db, "userPreferences", user.uid);
      await updateDoc(userDocRef, { marketingOptIn: newOptInValue });

      setSaveStatus("Saved successfully!");
      setTimeout(() => setSaveStatus(null), 2000); // Hide message after 2 seconds
    } catch (error) {
      console.error("Error updating marketing preference:", error);
      setSaveStatus("Failed to save. Try again.");
    }
  };

  const reauthenticateAndDelete = async () => {
    if (!user || !password) {
      alert("Please enter your password to proceed.");
      return;
    }

    try {
      // Reauthenticate the user
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      console.log("User reauthenticated successfully.");

      const db = getFirestore();
      const userDocRef = doc(db, "userPreferences", user.uid);

      // Delete the user's Firestore document
      await deleteDoc(userDocRef);
      console.log("User preferences deleted successfully.");

      // Delete the Firebase user
      await deleteUser(user);
      console.log("User account deleted successfully.");

      alert("Your account has been deleted successfully.");
      window.location.href = "/";
    } catch (error) {
      console.error("Error during reauthentication or deletion:", error.message);
      alert(
        "There was an error reauthenticating or deleting your account. Please ensure your password is correct or contact support."
      );
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/"; // Redirect to the login page after logout
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  };

  const handleCancelSubscription = async () => {
    if (!profile.subscription?.id) {
      console.error("No subscription ID found.");
      return;
    }

    try {
      const response = await fetch(`${serverUrl}/api/stripe/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: profile.subscription.id }),
      });

      if (!response.ok) {
        throw new Error("Failed to cancel subscription");
      }

      const result = await response.json();
      console.log("Subscription cancellation scheduled:", result);

      // Update UI to reflect the pending cancellation
      setProfile((prev) => ({
        ...prev,
        subscription: {
          ...prev.subscription,
          status: "pending cancellation",
        },
      }));
    } catch (error) {
      console.error("Error cancelling subscription:", error.message);
    }
  };



  return (
    <div className="properties-page">
      <header className="properties-header">
        <a href='./properties' style={{height:'20px',}}><img src='/imgs/domorango.png'  alt="My" className="header-logo" /></a>
      </header>
      <div className="my-account-content">
      {isLoading ? (
        <section>
              <h2>About Me</h2>
              <p>Loading...</p>
            </section>
      ) : user ? (
        <div>
            <section>
              <h2>About Me</h2>
              <p><strong>Email:</strong> {user.email}</p>
              <p style={{marginTop:"10px"}}><b>Marketing preferences</b></p>
              <label style={{ display: "flex", alignItems: "center", marginTop: "0px" }}>
                <input 
                  type="checkbox" 
                  checked={marketingOptIn} 
                  onChange={handleMarketingToggle} 
                  style={{ marginRight: "10px" }} 
                />
                Yes, I'm happy to receive emails about Domogogo
              </label>
              {saveStatus && <p style={{ color: saveStatus.includes("Failed") ? "red" : "green", marginTop: "5px" }}>{saveStatus}</p>}
            </section>

            <section>
              <h2>My Subscription</h2>
              <p>
                <b>Tier:</b> {(profile.tier ? profile.tier.charAt(0).toUpperCase() + profile.tier.slice(1) : "Not Assigned")}
              </p>
          {profile.subscription ? (
            <div>
              <p><b>Subscription Status:</b> {profile.subscription.status || "Unknown"}</p>
              <p>
                <b>Subscription Start Date:</b>{" "}
                {profile.subscription.startDate
                  ? profile.subscription.startDate.toDate().toLocaleDateString()
                  : "Not Available"}
              </p>
              <p>
                <b>Subscription End Date:</b>{" "}
                {profile.subscription.endDate
                  ? profile.subscription.endDate.toDate().toLocaleDateString()
                  : "--"}
              </p>
            </div>
          ) : (
            <p></p>
          )}
         
          {profile.subscription?.status === "active" && (
              <nav>
              <div style={{ marginTop: "15px" }}>
                <button
                  onClick={() => {
                    const confirmCancel = window.confirm("Are you sure you want to cancel your subscription?");
                    if (confirmCancel) {
                      handleCancelSubscription();
                    }
                  }}
                  className="property-action-button"
                >
                  Cancel Subscription
                </button>
              </div>
              <br />
            </nav>            
          )}
          
          
          </section>
          <div>
            <section>
              <h2>Actions</h2>
              <button onClick={handleLogout} className="property-action-button" style={{marginBottom:"10px"}}>
                  LOGOUT
              </button>
              <br/>
              <a
                href="#delete"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPasswordInput(true);
                }}
                style={{ color: 'red', textDecoration: 'underline', cursor: 'pointer' }}
              >
                Delete my account
              </a>

          {showPasswordInput && (
            <div style={{ marginTop: "20px" }}>
              <p><b>Please enter your password to confirm deletion of your account</b></p>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ padding: "10px", width: "100%", marginBottom: "10px" }}
              />
              <button onClick={reauthenticateAndDelete} className="property-action-button-red">
                Submit
              </button>
              <button
                onClick={() => {
                  setShowPasswordInput(false);
                  setPassword("");
                }}
                style={{
                  marginLeft: "10px",
                  padding: "10px",
                  backgroundColor: "#888888",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          )}
            </section>
          </div>

        </div>
        
      ) : (
        <p></p>
      )}
    </div>
    <Footer />
    </div>
  );
}


export default MyAccount;
